import { useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import {
    Card,
    CardContent,
} from "@component/card";
import {
    BuildingIcon,
} from "lucide-react";
import { EmployerDomainVerification } from "@type/auth";
import { setCookie } from "@util/cookie";
import { authResendCode, verifyHREmail } from "@service/auth";
import { catchErrorLog } from "@util/sentry";


interface Props {
    userEmail: string;
    domainVerification: EmployerDomainVerification | undefined
    onVerify: () => void;
}

export default function JoinTeamVerificationV2(props: Props) {
    const inviteMemberEmail = sessionStorage.getItem('__INVITE_MEMBER_EMAIL__') || "";
    const [digitInputs, setDigitInputs] = useState(["", "", "", "", "", ""]);
    const [verificationCode, setVerificationCode] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoadingResendCode, setLoadingResendCode] = useState<boolean>(false)

    const handleVerify = async () => {
        setError(null);
        setIsVerifying(true);

        verifyHREmail(props.userEmail, verificationCode).then((resp) => {
            if (resp.status === 200) {
                setCookie("authToken", resp.data.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);
                setIsVerified(true);
                props.onVerify();
            }
        }).catch((error) => {
            setError("Verification failed. Please check your code and try again.");
            catchErrorLog(`failed to verify HR email, err: ${error}`);
        }).finally(() => {
            setIsVerifying(false);
        })
    };

    const handleResendCode = async () => {
        setLoadingResendCode(true);
        await authResendCode(props.userEmail);
        setLoadingResendCode(false);
    };

    const handleDigitChange = (index: number, value: string) => {
        // Only allow single digits
        if (value.length > 1) return;
        if (value && !/^[0-9]$/.test(value)) return;

        const newDigits = [...digitInputs];
        newDigits[index] = value;
        setDigitInputs(newDigits);

        // Update the main verification code
        const code = newDigits.join("");
        setVerificationCode(code);

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`digit-${index + 1}`);
            nextInput?.focus();
        }
    };

    const handleDigitKeyDown = (index: number, e: React.KeyboardEvent) => {
        // Handle backspace to move to previous input
        if (e.key === "Backspace" && !digitInputs[index] && index > 0) {
            const prevInput = document.getElementById(`digit-${index - 1}`);
            prevInput?.focus();
        }
    };

    const handleDigitPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        const digits = pastedData.replace(/\D/g, "").slice(0, 6).split("");

        const newDigits = [...digitInputs];
        digits.forEach((digit, index) => {
            if (index < 6) {
                newDigits[index] = digit;
            }
        });

        // Fill remaining with empty strings
        for (let i = digits.length; i < 6; i++) {
            newDigits[i] = "";
        }

        setDigitInputs(newDigits);
        setVerificationCode(newDigits.join(""));

        // Focus the next empty input or the last one
        const nextEmptyIndex = newDigits.findIndex((digit) => digit === "");
        const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
        const targetInput = document.getElementById(`digit-${focusIndex}`);
        targetInput?.focus();
    };

    return (
        <Card className="w-full max-w-4xl">
            <CardContent className="space-y-6">
                {/* Team Found Section */}
                <div
                    className="text-center space-y-4"
                    style={{ paddingTop: "24px", paddingBottom: "0px" }}
                >
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                        <BuildingIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {inviteMemberEmail === "" ? "Join Your Team" : "Verify Your Email"}
                        </h3>
                        {inviteMemberEmail === "" && (
                            <p className="text-sm text-gray-600">
                                We found an existing workspace linked to your email.
                                <br />
                                Verify now to join.
                            </p>
                        )}
                    </div>
                </div>

                {/* Team Members Section */}
                {inviteMemberEmail === "" && (
                    <div className="space-y-3">
                        {/* Reusing workspace invitation member display style */}
                        <div
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                            style={{
                                backgroundColor: "#f3f4f6",
                                border: "0.909091px solid #d1d5db",
                            }}
                        >
                            <div className="flex items-center space-x-3 h-12">
                                <div className="h-12 w-12 overflow-hidden flex items-center justify-center rounded-full border border-gray-300 bg-white">
                                    <img
                                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${props.domainVerification?.company_logo}`}
                                        onError={(e) => {
                                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                        }}
                                    />
                                </div>

                                <div>
                                    <span className="text-sm text-gray-900 font-bold">{props.domainVerification?.company_name}</span>
                                </div>
                            </div>
                            <span className="text-sm text-gray-500 h-12 flex items-center">
                                <div className="flex items-center space-x-2">
                                    <span style={{ color: "#000000" }}>
                                        {props.domainVerification?.domain_member_count} member
                                        {props.domainVerification?.domain_member_count !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            </span>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {/* Code Sent Confirmation */}
                    <div className="text-center space-y-3">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                We've sent a verification code to
                            </p>
                            <p className="text-sm text-gray-600 font-bold">
                                {props.userEmail}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                                Enter verification code
                            </label>
                            <div className="flex justify-center space-x-3 mb-3">
                                {digitInputs.map((digit, index) => (
                                    <Input
                                        key={index}
                                        id={`digit-${index}`}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={digit}
                                        onChange={(e) => handleDigitChange(index, e.target.value)}
                                        onKeyDown={(e) => handleDigitKeyDown(index, e)}
                                        onPaste={handleDigitPaste}
                                        className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
                                        maxLength={1}
                                        required={index === 0}
                                    />
                                ))}
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                        </div>

                        <Button
                            onClick={handleVerify}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            disabled={isVerifying || isVerified}
                        >
                            {isVerifying ? "Verifying..." : "Verify and Join Team"}
                        </Button>
                    </div>

                    {/* Action Links */}
                    <div className="flex justify-center items-center text-sm">
                        <button
                            onClick={handleResendCode}
                            disabled={isLoadingResendCode}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            {isLoadingResendCode ? "Sending..." : "Resend code"}
                        </button>
                    </div>
                </div>

                {/* Agreement Text */}
                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        By verifying your email, you agree to join the existing workspace at{" "}
                        {props.domainVerification?.company_name}.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
